import { L10n } from '../L10n/L10n';
import { L10nScriptLoader } from '../utils/L10nScriptLoader';
import { Tableau } from '../utils/TableauLog';

const supportedLocales: { [legacyLanguageLocale: string]: string } = {
  de: 'de_DE',
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR',
  ga: 'ga_IE',
  it: 'it_IT',
  ja: 'ja_JP',
  ko: 'ko_KR',
  nl: 'nl_NL',
  pt: 'pt_BR',
  sv: 'sv_SE',
  th: 'th_TH',
  zh: 'zh_CN',

  ar_YE: 'ar_YE',
  de_DE: 'de_DE',
  en_GB: 'en_GB',
  en_US: 'en_US',
  es_ES: 'es_ES',
  fr_CA: 'fr_CA',
  fr_FR: 'fr_FR',
  ga_IE: 'ga_IE',
  it_IT: 'it_IT',
  ja_JP: 'ja_JP',
  ko_KR: 'ko_KR',
  nl_NL: 'nl_NL',
  pt_BR: 'pt_BR',
  sv_SE: 'sv_SE',
  th_TH: 'th_TH',
  zh_CN: 'zh_CN',
  zh_TW: 'zh_TW'
};

/**
 * The role of this class is to ensure the right message and locale js files are
 * loaded in VizportalClient.
 */
class LocalizationLoaderService {

  private defaultLanguage = 'en';
  private defaultLocale = 'en_US';

  public selectLocaleOrDefault(k: string): string {
    return supportedLocales[k] || this.defaultLocale;  // clearly communicates default
  }

  public updateLanguageAndLocaleInClient(language: string, locale: string): Promise<any> {
    // Intentially not setup as default parameters, want them to be required.
    const newLanguage = language || this.defaultLanguage;
    const newLocale = locale || this.defaultLocale;

    const languagePromise = this.maybeUpdateLanguageInClient(newLanguage).catch(() => {
      Tableau.Logger().error(`Failed to read UI strings for '${newLanguage}'. Falling back to '${this.defaultLanguage}'`);
      // TODO: ideally perhaps we'd revert the language update on the server here?
      this.maybeUpdateLanguageInClient(this.defaultLanguage);
    });
    const localePromise = this.maybeUpdateLocaleInClient(newLocale).catch(() => {
      this.maybeUpdateLocaleInClient(this.defaultLocale);
    });

    return Promise.all([languagePromise, localePromise]);
  }

  private maybeUpdateLanguageInClient(newLanguage: string): Promise<any> {
    const oldLanguage = L10n.language;

    if (newLanguage !== oldLanguage) {
      const messageLocaleId = this.selectLocaleOrDefault(newLanguage);
      if (!supportedLocales[newLanguage]) {
        Tableau.Logger().error(`Unrecognized language '${newLanguage}'. Falling back to '${this.defaultLocale}'`);
      }
      L10n.language = messageLocaleId.slice(0, 2);
      // Keep html lang attribute synchronized with the current language
      document.querySelector('html').setAttribute('lang', L10n.language);
      document.querySelector('html').setAttribute('xml:lang', L10n.language);
      return L10nScriptLoader.getLocalizeMessages(messageLocaleId);
    }

    return Promise.resolve();
  }

  private maybeUpdateLocaleInClient(newLocale: string): Promise<any> {
    const oldLocale = L10n.locale;
    if (newLocale !== oldLocale) {
      L10n.locale = newLocale;
      return L10nScriptLoader.getLocalizeFormattersAndParsers(newLocale);
    }
    return Promise.resolve();
  }
}

export const LocalizationLoader = new LocalizationLoaderService();
